import { useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { aiFeedback, useAiFeature } from "@/lib/ai";
import { AiButton } from "./AiButton";

interface Tidied {
  suggestion_id: string;
  summary: string;
  customer_needs: string[];
  next_step: string | null;
  follow_up_at: string | null;
}

/**
 * "Tidy with AI" beside a notes box: the rep's quick notes come back as a clear summary with the needs and the next step,
 * written into the box for the rep to check. Undo puts their own words back.
 */
export function TidyNotesButton({
  text,
  kind,
  onTidied,
}: {
  text: string;
  kind: "VISIT" | "MEETING" | "CALL" | "NOTE";
  /** The new notes, and a follow-up date (YYYY-MM-DD) when the notes imply one */
  onTidied: (notes: string, followUpDate: string | null) => void;
}) {
  const available = useAiFeature("TIDY_NOTES");
  const { error } = useToast();
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState<{ before: string; id: string } | null>(null);
  if (!available) return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = unwrapObject<Tidied>(await api.post("/api/Ai/TidyNotes", { text, kind }));
      const next = [r.summary, r.customer_needs.length ? `Needs: ${r.customer_needs.join("; ")}` : "", r.next_step ? `Next step: ${r.next_step}` : ""].filter(Boolean).join("\n");
      setUndo({ before: text, id: r.suggestion_id });
      aiFeedback(r.suggestion_id, true);
      onTidied(next, r.follow_up_at);
    } catch (e: any) {
      error("Couldn't tidy the notes", e.message || "Please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <AiButton busy={busy} disabled={!text.trim()} onClick={run} title="Rewrite these notes clearly, with the needs and the next step">
        Tidy with AI
      </AiButton>
      {undo && (
        <button
          type="button"
          className="text-xs text-muted-foreground underline hover:text-foreground"
          onClick={() => {
            aiFeedback(undo.id, false);
            onTidied(undo.before, null);
            setUndo(null);
          }}
        >
          Undo
        </button>
      )}
    </span>
  );
}
